/**
 * Customer-facing economics wording.
 *
 * Three independent concepts stay separate — never collapse them:
 *   product identity confidence  ("Exact" / "Needs review" / …)
 *   economics completeness       (these labels)
 *   opportunity qualification    (WINNING / Manual review / …)
 *
 * A product can have complete economics and still need Manual Review, and a
 * verified identity can still lack complete fee data.
 */
export const ECONOMICS_KIND_LABELS = {
  complete_net_profit: "Complete — all fees included",
  complete_with_disclosed_assumptions: "Complete estimate — documented assumptions",
  fee_excluded_estimate: "Estimated — fulfillment fees excluded",
  fee_completeness_unknown: "Fee completeness not established",
  incomplete: "Economics incomplete",
};

export const ECONOMICS_KIND_SHORT = {
  complete_net_profit: "Complete",
  complete_with_disclosed_assumptions: "Complete (assumptions)",
  fee_excluded_estimate: "Estimated",
  fee_completeness_unknown: "Unclassified",
  incomplete: "Incomplete",
};

export const ECONOMICS_KIND_TONE = {
  complete_net_profit: "success",
  complete_with_disclosed_assumptions: "success",
  fee_excluded_estimate: "warning",
  fee_completeness_unknown: "neutral",
  incomplete: "neutral",
};

/** Readable label for a raw `economics_kind`, or null when nothing is known. */
export function economicsKindLabel(kind) {
  const key = String(kind || "").trim();
  if (!key) return null;
  return ECONOMICS_KIND_LABELS[key] || "Economics classification unavailable";
}

export function economicsKindShort(kind) {
  const key = String(kind || "").trim();
  return ECONOMICS_KIND_SHORT[key] || null;
}

export function economicsKindTone(kind) {
  return ECONOMICS_KIND_TONE[String(kind || "").trim()] || "neutral";
}

/**
 * Explanatory line for a customer, derived from the persisted classification
 * plus the boolean flags the API already exposes.
 */
export function economicsExplanation({ economicsKind, economicsComplete, feeComplete, excludesWfs } = {}) {
  if (economicsKind === "complete_with_disclosed_assumptions") {
    return "Fees come from the published schedule plus assumptions your profile documents; storage is disclosed, not observed.";
  }
  if (economicsKind === "complete_net_profit") {
    return "Every cost component is evidenced for this calculation.";
  }
  if (economicsKind === "fee_completeness_unknown") {
    return "This record was saved before fee completeness was tracked. The profit is preserved as-is; we cannot confirm which fees it included.";
  }
  if (economicsKind === "fee_excluded_estimate" || (economicsComplete === false && excludesWfs)) {
    return "Referral fee is included. Fulfillment (and storage) are not, so this is an estimate, not net profit.";
  }
  if (feeComplete === false) {
    return "Some fee inputs are missing, so this number is an estimate.";
  }
  return null;
}
